import React from 'react';
import { Phone, Mail, MessageCircle, Linkedin, Building, User, DollarSign, Calendar } from 'lucide-react';
import { toLocaleDateStringInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';

interface LeadCardProps {
  lead: any;
  onClick: () => void;
  onDragStart: (e: React.DragEvent, lead: any) => void;
}

function formatDealValue(val: number | null | undefined): string | null {
  if (val == null || val <= 0) return null;
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
}

const LeadCard: React.FC<LeadCardProps> = ({ lead, onClick, onDragStart }) => {
  const tz = useTimezone();
  const getChannelTag = (channel: string) => {
    if (!channel || channel.toString().trim() === '' || channel === 'undefined' || channel === 'null') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
          <MessageCircle size={10} className="mr-1" />
          Unknown
        </span>
      );
    }

    const normalized = channel.toString().toLowerCase().trim();
    switch (normalized) {
      case 'email':
      case 'email agent':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border-2 border-blue-200/50"><Mail size={10} className="mr-1" />Email</span>;
      case 'call':
      case 'call agent':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-[#1875FF]/20 text-[#1875FF] border-2 border-[#1875FF]/50"><Phone size={10} className="mr-1" />Call</span>;
      case 'whatsapp':
      case 'whatsapp agent':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-[#52B5AE]/20 text-[#52B5AE] border-2 border-[#52B5AE]/50"><MessageCircle size={10} className="mr-1" />WhatsApp</span>;
      case 'linkedin':
      case 'linkedin agent':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-[#05E0D1]/20 text-[#05E0D1] border-2 border-[#05E0D1]/50"><Linkedin size={10} className="mr-1" />LinkedIn</span>;
      default:
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"><MessageCircle size={10} className="mr-1" />{channel}</span>;
    }
  };

  const getStatusTag = (status: string) => {
    const normalized = (status || '').toLowerCase().trim();
    switch (normalized) {
      case 'chatting':
      case 'talking':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-[#52B5AE]/20 text-[#52B5AE] border-2 border-[#52B5AE]/50">{status}</span>;
      case 'follow up':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-[#1875FF]/20 text-[#1875FF] border-2 border-[#1875FF]/50">Follow Up</span>;
      case 'booked':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-[#05E0D1]/20 text-[#05E0D1] border-2 border-[#05E0D1]/50">Booked</span>;
      default:
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">{status || 'Open'}</span>;
    }
  };

  const dealValueFormatted = formatDealValue(lead.deal_value);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onClick={onClick}
      className="bg-white rounded-lg p-3.5 cursor-move hover:shadow-lg transition-all duration-300 ease-in-out border-2 border-[#1875FF]/40 hover:border-[#05E0D1]/40 group hover:scale-[1.02] active:opacity-50 active:cursor-grabbing active:scale-95 active:rotate-2"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2 min-w-0">
          <User size={14} className="text-gray-400 group-hover:text-[#1875FF] transition-colors flex-shrink-0" />
          <span className="font-semibold text-gray-800 text-sm truncate">
            {lead.dm_full_name || 'Unknown'}
          </span>
        </div>
        {dealValueFormatted && (
          <span className="flex items-center text-xs font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200/50 flex-shrink-0 ml-2 font-metric">
            <DollarSign size={10} className="mr-0.5" />
            {dealValueFormatted.replace('$', '')}
          </span>
        )}
      </div>

      <div className="flex items-center space-x-2 mb-2">
        <Building size={14} className="text-gray-400 group-hover:text-[#05E0D1] transition-colors flex-shrink-0" />
        <span className="text-sm text-gray-600 font-medium truncate">
          {lead.company_name || 'Unknown Company'}
        </span>
      </div>

      {lead.industry_1 && (
        <div className="text-xs text-gray-500 mb-3 pl-5 truncate">
          {lead.industry_1}
        </div>
      )}

      <div className="flex items-center justify-between mb-2.5 gap-2">
        <div className="flex items-center">
          {getChannelTag(lead.channel_booked)}
        </div>
        <div className="flex items-center">
          {getStatusTag(lead.ovrall_lead_status)}
        </div>
      </div>

      <div className="mt-2.5 pt-2.5 border-t-2 border-[#1875FF]/10 space-y-1">
        {lead.sales_person && (
          <div className="text-xs text-gray-500">
            <span className="font-medium text-gray-600">Rep:</span> {lead.sales_person}
          </div>
        )}
        {lead.meeting_date && (
          <div className="flex items-center space-x-1 text-xs text-[#1875FF]">
            <Calendar size={10} />
            <span><span className="font-semibold">Meeting:</span> {toLocaleDateStringInTz(lead.meeting_date, tz)}</span>
          </div>
        )}
        {!lead.sales_person && !lead.meeting_date && (
          <div className="text-xs text-gray-400">No recent activity</div>
        )}
      </div>
    </div>
  );
};

export default LeadCard;
